import { IconClockHour5 } from '@tabler/icons-react'

import { AstraButton, AstraIcon, AstraTooltip, day, getOnlyDate, humanizeDate, StickerNoteIcon } from '~/astra'
import { useLocationNotes } from '~/hooks/useLocationNotes'
import { useLocationScheduleSpecialities, usePractitionerSchedule, usePractitionerScheduleLocations, usePractitionerScheduleStatuses } from '~/storm'

import { DateLocationCell, DatePractitionerCell } from '.'

type NoteProps = {
    openNote: () => void
    isSingleCell: boolean
    hasNote: boolean
}

const Note = ({ openNote, isSingleCell, hasNote }: NoteProps) => {
    const text = isSingleCell ? (hasNote ? 'Endre notat' : 'Legg til notat') : 'Du kan ikke skrive kommentar på flere dager samtidig. Velg kun én dag.'
    return (
        <AstraTooltip tooltipContent={text}>
            <AstraButton variant="ghost" size="medium" onClick={openNote} disabled={!isSingleCell} icon={<StickerNoteIcon className="size-6" />} />
        </AstraTooltip>
    )
}

type ActivityLogProps = {
    openActivityLog: () => void
    isSingleCell: boolean
}

const ActivityLog = ({ openActivityLog, isSingleCell }: ActivityLogProps) => {
    const text = isSingleCell ? 'Vis aktivitetslogg' : 'Du kan ikke se aktivitetslogg for flere dager samtidig. Velg kun én dag.'
    return (
        <AstraTooltip tooltipContent={text}>
            <AstraButton
                variant="ghost"
                size="medium"
                onClick={openActivityLog}
                disabled={!isSingleCell}
                icon={<AstraIcon icon={IconClockHour5} size="large" />}
            />
        </AstraTooltip>
    )
}

type LatestUpdatedData = { updated_at: string; updated_by?: string | null }
function getLatestUpdatedAtAndBy(schedules: LatestUpdatedData[]) {
    return schedules
        .map(({ updated_at, updated_by }) => ({ updated_at, updated_by }))
        .toSorted((a, b) => b.updated_at.localeCompare(a.updated_at))
        .at(0)
}

const LastUpdated = ({ updated_at, updated_by }: LatestUpdatedData) => {
    const humanizedDate = humanizeDate(day(updated_at))
    return (
        <AstraTooltip tooltipContent={updated_by ? `Sist endret av ${updated_by} ${humanizedDate}` : `Sist endret ${humanizedDate}`}>
            <p className="cursor-pointer text-xs text-gray-500 hover:underline">{humanizedDate}</p>
        </AstraTooltip>
    )
}

type PractitionerProps = {
    selectedCells: [DatePractitionerCell, ...DatePractitionerCell[]] // array with at least one element
    openNote: () => void
    openActivityLog: () => void
}

const BaseBottomBar = ({
    onOpenNote: onOpenNote,
    openActivityLog,
    isSingleCell,
    latestUpdatedData,
    hasNote = false,
}: {
    onOpenNote?: () => void
    openActivityLog: () => void
    isSingleCell: boolean
    hasNote?: boolean
    latestUpdatedData?: LatestUpdatedData
}) => (
    <div className="pointer-events-auto flex items-center justify-between px-3 py-1">
        <div className="flex justify-start gap-2">
            {onOpenNote && <Note openNote={onOpenNote} isSingleCell={isSingleCell} hasNote={hasNote} />}
            <ActivityLog openActivityLog={openActivityLog} isSingleCell={isSingleCell} />
        </div>
        {latestUpdatedData && <LastUpdated updated_at={latestUpdatedData.updated_at} updated_by={latestUpdatedData.updated_by} />}
    </div>
)

export const PractitionerBottomBar = ({ selectedCells, openNote, openActivityLog }: PractitionerProps) => {
    const firstCell = selectedCells[0]
    const singleCell = selectedCells.length === 1

    const allScheduleStatusesByCell = usePractitionerScheduleStatuses({
        where: {
            OR: selectedCells.map(({ date, practitionerId }) => ({ scheduled_date: getOnlyDate(date), practitioner_id: practitionerId })),
        },
    })
    const allScheduleLocationsByCell = usePractitionerScheduleLocations({
        where: {
            OR: selectedCells.map(({ date, practitionerId }) => ({ scheduled_date: getOnlyDate(date), practitioner_id: practitionerId })),
        },
    })
    const latestUpdatedAtAndBy = getLatestUpdatedAtAndBy([...allScheduleStatusesByCell, ...allScheduleLocationsByCell])

    const firstCellSchedule = usePractitionerSchedule({ where: { scheduled_date: getOnlyDate(firstCell.date), practitioner_id: firstCell.practitionerId } })
    const selectedCellNote = singleCell ? firstCellSchedule?.comment : null

    return (
        <BaseBottomBar
            onOpenNote={openNote}
            hasNote={!!selectedCellNote}
            openActivityLog={openActivityLog}
            isSingleCell={singleCell}
            latestUpdatedData={latestUpdatedAtAndBy}
        />
    )
}

type LocationProps = {
    selectedCells: [DateLocationCell, ...DateLocationCell[]] // array with at least one element
    openNote: () => void
    openActivityLog: () => void
}

export const LocationBottomBar = ({ selectedCells, openNote, openActivityLog }: LocationProps) => {
    const selectedCell = selectedCells[0]
    const { note } = useLocationNotes({ location: selectedCell })

    const allScheduleLocationsByCell = usePractitionerScheduleLocations({
        where: {
            OR: selectedCells.map(({ date, locationId }) => ({ scheduled_date: getOnlyDate(date), location_id: locationId })),
        },
    })
    const latestUpdatedAtAndBy = getLatestUpdatedAtAndBy(allScheduleLocationsByCell)

    const singleCell = selectedCells.length === 1

    return (
        <BaseBottomBar
            onOpenNote={openNote}
            hasNote={!!note}
            openActivityLog={openActivityLog}
            isSingleCell={singleCell}
            latestUpdatedData={latestUpdatedAtAndBy}
        />
    )
}

type AllocationProps = {
    selectedCells: [DateLocationCell, ...DateLocationCell[]] // array with at least one element
    openActivityLog: () => void
}

export const AllocationsBottomBar = ({ selectedCells, openActivityLog }: AllocationProps) => {
    const allocationsByCell = useLocationScheduleSpecialities({
        where: {
            OR: selectedCells.map(({ date, locationId }) => ({ scheduled_date: getOnlyDate(date), location_id: locationId })),
        },
    })
    const latestUpdatedAtAndBy = getLatestUpdatedAtAndBy(allocationsByCell)

    return <BaseBottomBar openActivityLog={openActivityLog} isSingleCell={selectedCells.length === 1} latestUpdatedData={latestUpdatedAtAndBy} />
}
